import PhonePicker from 'components/molecules/PhonePicker';
import { BUTTON_NAMES } from 'constant/button-names';
import useRecaptchaToken from 'hooks/useRecaptchaToken';
import { useSendVerificationCode } from 'pages/queries/customer.queries';
import React, { useCallback, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ButtonRow, StyledFormLabel, StyledRoudedEdgesButton } from './styles';

interface PhoneForm {
  phoneNumber: string;
}

interface PhoneNumberFormProps {
  onSuccess: (phone: string, country: string) => void;
  autoFocus?: boolean;
  children?: React.ReactNode;
}

const PhoneNumberForm: React.FC<PhoneNumberFormProps> = ({ onSuccess, children, autoFocus = false }) => {
  const { t } = useTranslation();
  const [country, setCountry] = useState<string>('AE');
  const { mutateAsync: sendVerificationCode } = useSendVerificationCode();
  const fetchedRecaptchaToken = useRecaptchaToken();
  const { register, handleSubmit, control, setValue, formState } = useForm<PhoneForm>({
    mode: 'onChange',
    defaultValues: {
      phoneNumber: '',
    },
  });

  const { isDirty, isValid } = formState;

  const phoneNumber = useWatch({
    control,
    name: 'phoneNumber',
    defaultValue: '',
  });

  React.useEffect(() => {
    register('phoneNumber', {
      required: true,
      minLength: {
        value: 9,
        message: t('profilePage.editPhoneValidationMessage'),
      },
    });
  }, [register, t]);

  const onSubmit =
    () =>
    async ({ phoneNumber: phone }: PhoneForm) => {
      const recaptchaToken = await fetchedRecaptchaToken();
      sendVerificationCode({
        phone,
        recaptchaToken,
      })
        .then(() => {
          onSuccess(phone, country);
        })
        .catch((error) => {
          toast.dark(error.message);
        });
    };

  const onCountryChange = useCallback((newCountryValue: string) => {
    setCountry(newCountryValue);
  }, []);

  const onChangePhoneNumber = useCallback((phone: string) => setValue('phoneNumber', phone, { shouldValidate: true }), [setValue]);

  return (
    <Form>
      <Form.Group controlId="formPhoneNumber">
        <StyledFormLabel>{t('profilePage.mobileNumber')}</StyledFormLabel>
        <PhonePicker
          autoFocus={autoFocus}
          defaultCountry="AE"
          placeholder={t('profilePage.enterMobileNumber')}
          onChange={onChangePhoneNumber}
          defaultValue={phoneNumber}
          onCountryChange={onCountryChange}
        />
      </Form.Group>
      {children}
      <ButtonRow>
        <StyledRoudedEdgesButton
          buttonName={BUTTON_NAMES.CONTINUE}
          onClick={handleSubmit(onSubmit())}
          type="button"
          size="lg"
          block
          disabled={isDirty || !isValid}
        >
          {t('common.continue')}
        </StyledRoudedEdgesButton>
      </ButtonRow>
    </Form>
  );
};

export default PhoneNumberForm;
